/**
 * An account as a reason sentence names it: its name plus the last four digits
 * of its number. Two cards from one bank often share a name, and the number is
 * what tells the user which of them the statement is about.
 */
export function accountLabel(account) {
  if (!account) return null;
  const number = account.external_account_number;
  return number ? `${account.name} ··${String(number).slice(-4)}` : account.name;
}

/**
 * Which linked bank row contradicts the movement, and which way it points:
 * `{ account, accountId, statement: 'in'|'out' }` or null. The direction is the
 * one a classification rule decided on the server, the same polarity the
 * backend's direction check judges, so the sentence explains the real reason
 * instead of re-deriving it from the amount's sign.
 */
export function directionEvidence(movement, edited, accounts = []) {
  const type = edited?.type;
  for (const link of movement?.statement_links || []) {
    if (!link.direction || link.account_id == null) continue;   // no rule decided it
    const acc = accounts.find((a) => a.id === link.account_id);
    if (!acc) continue;
    if (type === 'Transfer') {
      if (link.direction === 'in' && link.account_id === edited?.from_account_id) {
        return { account: accountLabel(acc), accountId: acc.id, statement: 'in' };   // arrived in the "from" leg
      }
      if (link.direction === 'out' && link.account_id === edited?.to_account_id) {
        return { account: accountLabel(acc), accountId: acc.id, statement: 'out' };  // left the "to" leg
      }
    } else if (link.account_id === edited?.account_id) {
      if (type === 'Income' && link.direction === 'out') return { account: accountLabel(acc), accountId: acc.id, statement: 'out' };
      if (type === 'Expenses' && link.direction === 'in') return { account: accountLabel(acc), accountId: acc.id, statement: 'in' };
    }
  }
  return null;
}

/**
 * Whether a movement holds two readings of one bank row pointing opposite
 * ways: on one account, a linked row the rules read as money in and another
 * as money out. That is what a statement read twice leaves on a movement.
 */
export function hasOppositeReadings(movement) {
  const directions = {};
  for (const link of movement?.statement_links || []) {
    if (!link.direction || link.account_id == null) continue;
    (directions[link.account_id] = directions[link.account_id] || new Set()).add(link.direction);
  }
  return Object.values(directions).some((seen) => seen.size > 1);
}

/**
 * Whether a linked bank row sits on an account the movement does not use.
 * Mirrors the backend's has_account_mismatch rule, over the edited accounts so
 * a form can clear it the moment the right account is picked.
 */
export function hasAccountMismatch(movement, edited) {
  const type = edited?.type;
  const links = movement?.statement_links || [];
  if (links.length === 0) return false;
  let allowed;
  if (type === 'Income' || type === 'Expenses') {
    if (edited.account_id == null) return false;
    allowed = [edited.account_id];
  } else if (type === 'Transfer') {
    allowed = [edited.from_account_id, edited.to_account_id];
  } else {
    return false;
  }
  return links.some((link) => link.account_id != null && !allowed.includes(link.account_id));
}

/**
 * Every active review alert on a movement, as the sentence that explains it:
 * `[{ flag, text, tone }]`, where `flag` is the backend review flag and `tone`
 * is 'alert' (something to fix) or 'muted' (only waiting for the bank).
 *
 * Shared by the movement detail panel and the Review cards. `edited` is the
 * movement as the user is editing it (defaults to the saved one), so the
 * account and direction sentences follow the fields before they are saved.
 * `prefs` is the user's alert map; a key is on unless it is `false`. `t` is the
 * movements-namespace translator. `readTwiceFileId` is the statement, read
 * twice, whose extra reading the movement holds (`useReadTwiceStatement`): its
 * sentence carries `fixFileId`, for a way into that statement's checkup.
 */
export function movementAlertReasons(movement, {
  accounts = [], t, prefs = {}, edited = movement, readTwiceFileId = null,
} = {}) {
  if (!movement) return [];
  const type = edited?.type;
  const on = (key) => prefs[key] !== false;
  const links = movement.statement_links || [];
  const find = (id) => accounts.find((a) => a.id === id);
  const list = [];
  const push = (flag, text, tone = 'alert', extra = {}) => list.push({ flag, text, tone, ...extra });

  const incomplete = (type === 'Income' || type === 'Expenses')
    ? (edited?.account_id == null || edited?.category_id == null)
    : type === 'Transfer'
      ? (edited?.from_account_id == null || edited?.to_account_id == null)
      : false;
  if (incomplete && on('incomplete')) push('is_incomplete', t('detailPanel.alerts.incomplete'));

  if (movement.is_half_confirmed && on('halfConfirmed')) {
    // The flag is the saved movement's: name its accounts, not the edited ones.
    const legs = [movement.from_account_id, movement.to_account_id];
    const confirmedId = links.find((link) => legs.includes(link.account_id))?.account_id;
    const confirmed = accountLabel(find(confirmedId));
    const other = accountLabel(find(legs.find((id) => id !== confirmedId)));
    push('is_half_confirmed', confirmed && other
      ? t('detailPanel.alerts.halfConfirmed', { confirmed, other })
      : t('detailPanel.alerts.halfConfirmedUnknown'));
  }

  if (hasAccountMismatch(movement, edited) && on('accountMismatch')) {
    const allowed = type === 'Transfer'
      ? [edited.from_account_id, edited.to_account_id]
      : [edited.account_id];
    const offending = links.find((link) => link.account_id != null && !allowed.includes(link.account_id));
    const stmt = accountLabel(find(offending?.account_id));
    if (type === 'Transfer') {
      const from = accountLabel(find(edited.from_account_id));
      const to = accountLabel(find(edited.to_account_id));
      push('has_account_mismatch', stmt && from && to
        ? t('detailPanel.alerts.accountMismatchTransfer', { statement: stmt, from, to })
        : t('detailPanel.alerts.accountMismatchUnknown'));
    } else {
      const account = accountLabel(find(edited.account_id));
      push('has_account_mismatch', stmt && account
        ? t('detailPanel.alerts.accountMismatch', { statement: stmt, account })
        : t('detailPanel.alerts.accountMismatchUnknown'));
    }
  }

  if (movement.needs_source_rule_review && on('sourceRules')) {
    const coverage = movement.source_rule_coverage || null;
    const evidence = directionEvidence(movement, edited, accounts);
    const acct = type === 'Transfer'
      ? (find(edited?.from_account_id) || find(edited?.to_account_id))
      : movement.account;
    // A coverage gap carries no rule direction, and neither does a movement
    // whose linked rows no rule decided: the source's rules leave the row
    // uncovered, so there is no contradiction to explain.
    const noRuleDirection = coverage === 'none'
      || coverage === 'sign_missing'
      || !links.some((link) => link.direction);
    if (readTwiceFileId != null && hasOppositeReadings(movement)) {
      // A statement read twice left its first reading here, the other way
      // round: the movement is right, the extra reading is what warns.
      push('needs_source_rule_review', t('detailPanel.alerts.sourceRulesReadTwice'), 'alert', { fixFileId: readTwiceFileId });
    } else if (coverage === 'sign_missing' && acct?.source) {
      push('needs_source_rule_review', t('detailPanel.alerts.sourceRulesSignMissing', { source: acct.source }));
    } else if (evidence) {
      // A rule that contradicts a leg is the real reason, even when the row
      // classified at import left a gap. Say which way the STATEMENT points,
      // not which way the movement does: that is the half the user has not seen.
      push('needs_source_rule_review', t(evidence.statement === 'in'
        ? 'detailPanel.alerts.sourceRulesArrivedButTakesOut'
        : 'detailPanel.alerts.sourceRulesLeftButBringsIn',
      { account: evidence.account }));
    } else if (noRuleDirection) {
      // Name the account of a row no rule covered, never one a rule did.
      const uncovered = links.find((link) => link.account_id != null && !link.direction);
      const stmt = accountLabel(find(uncovered?.account_id));
      push('needs_source_rule_review', stmt
        ? t('detailPanel.alerts.sourceRulesNone', { account: stmt })
        : t('detailPanel.alerts.sourceRulesNoneUnknown'));
    } else {
      push('needs_source_rule_review', t('detailPanel.alerts.sourceRulesContradictionUnknown'));
    }
  }

  if (movement.is_wrong_currency && on('wrongCurrency')) push('is_wrong_currency', t('detailPanel.alerts.wrongCurrency'));
  if (movement.is_exchange_missing && on('exchangeMissing')) push('is_exchange_missing', t('detailPanel.alerts.exchangeMissing'));
  if (movement.needs_source_file && on('needsSourceFile')) push('needs_source_file', t('detailPanel.alerts.needsSourceFile'), 'muted');

  return list;
}
